import React, { useEffect, useState } from "react";
import { CCol, CFormInput, CFormLabel, CFormSelect, CFormSwitch, CRow } from "@coreui/react";
import { useExchangeSymbols, useExchanges, useStrategies } from "@src/hooks/redux";
import { tdApi } from "@src/services/api/tdApi";
import type { CommonSettingsData } from "@src/types";
import { combineFilters, createFilter } from "@tradingdroid/api-client";

import { CDateTimePicker } from "./CDateTimePicker";

interface Props extends CommonSettingsData {
  onChange: (field: string, value: string | number | boolean) => void;
  disabled?: boolean;
  hideDates?: boolean;
}

export const CommonSettingsBlock: React.FC<Props> = ({
  strategyId,
  exchangeName,
  instrument,
  timeframe,
  startDateTime,
  endDateTime,
  investmentAmount,
  cumulativeInvestment,
  onChange,
  disabled = false,
  hideDates = false,
}) => {
  const { strategies } = useStrategies();
  const { exchanges } = useExchanges();
  const { getSymbolNamesForExchange, setSymbolNamesForExchange } = useExchangeSymbols();

  const [loadingSymbols, setLoadingSymbols] = useState(false);

  // Get symbol names from Redux (auto-updates on state change)
  const symbolNames = getSymbolNamesForExchange(exchangeName) ?? [];

  // Fetch symbol names when exchange changes (if not cached)
  useEffect(() => {
    if (!exchangeName) return;

    // Already cached in Redux
    if (getSymbolNamesForExchange(exchangeName)) return;

    let isCancelled = false;
    setLoadingSymbols(true);

    // Fetch only names with larger page size for speed
    // Use createFilter/combineFilters to safely build the query string (avoids injection)
    const exchangeFilter = combineFilters(createFilter("exchange", "EQ", exchangeName));
    tdApi
      .getAllSymbols({ q: exchangeFilter }, { fields: "name", limit: 500 })
      .then((symbols) => {
        if (isCancelled) return;
        const names = symbols.map((s) => s.name);
        setSymbolNamesForExchange(exchangeName, names);
      })
      .catch((err: unknown) => {
        if (isCancelled) return;
        console.error("Failed to fetch symbols:", err);
      })
      .finally(() => {
        if (!isCancelled) setLoadingSymbols(false);
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeName]);

  return (
    <div className="CommonSettingsBlock">
      <div className="mb-3">
        <CFormLabel>Strategy</CFormLabel>
        <CFormSelect
          value={strategyId}
          onChange={(e) => onChange("strategyId", Number(e.target.value))}
          disabled={disabled}
        >
          <option value={0}>
            {strategies.length === 0 ? "No strategies available" : "Select Strategy"}
          </option>
          {strategies.map((strategy) => (
            <option key={strategy.id} value={strategy.id}>
              {strategy.description || strategy.name}
            </option>
          ))}
        </CFormSelect>
      </div>

      <div className="mb-3">
        <CFormLabel>Select Exchange</CFormLabel>
        <CFormSelect
          value={exchangeName}
          onChange={(e) => onChange("exchangeName", e.target.value)}
          disabled={disabled}
        >
          <option value="">
            {exchanges.length === 0 ? "No exchanges available" : "Select Exchange"}
          </option>
          {exchanges.map((exchange) => (
            <option key={exchange.name} value={exchange.name}>
              {exchange.name}
            </option>
          ))}
        </CFormSelect>
      </div>

      <div className="mb-3">
        <CFormLabel>Instrument</CFormLabel>
        <CFormSelect
          value={instrument}
          onChange={(e) => onChange("instrument", e.target.value)}
          disabled={disabled || !exchangeName || loadingSymbols}
        >
          <option value="">
            {!exchangeName
              ? "Select an exchange first"
              : loadingSymbols
                ? "Loading symbols..."
                : symbolNames.length === 0
                  ? "No symbols available"
                  : "Select Instrument"}
          </option>
          {symbolNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </CFormSelect>
      </div>

      <div className="mb-3">
        <CFormLabel>Timeframe</CFormLabel>
        <CFormSelect
          value={timeframe}
          onChange={(e) => onChange("timeframe", e.target.value)}
          disabled={disabled}
        >
          <option value="1m">1m</option>
          <option value="3m">3m</option>
          <option value="5m">5m</option>
          <option value="15m">15m</option>
          <option value="30m">30m</option>
          <option value="1h">1h</option>
          <option value="2h">2h</option>
          <option value="4h">4h</option>
          <option value="6h">6h</option>
          <option value="8h">8h</option>
          <option value="12h">12h</option>
          <option value="1d">1d</option>
          <option value="3d">3d</option>
          <option value="1w">1w</option>
          <option value="1M">1M</option>
        </CFormSelect>
      </div>

      {!hideDates && startDateTime && endDateTime && (
        <CRow className="g-2">
          <CCol md={6}>
            <div className="mb-3">
              <CDateTimePicker
                label="Start Date & Time"
                value={startDateTime}
                onChange={(value) => onChange("startDateTime", value)}
                disabled={disabled}
                maxDate={endDateTime}
              />
            </div>
          </CCol>
          <CCol md={6}>
            <div className="mb-3">
              <CDateTimePicker
                label="End Date & Time"
                value={endDateTime}
                onChange={(value) => onChange("endDateTime", value)}
                disabled={disabled}
                minDate={startDateTime}
              />
            </div>
          </CCol>
        </CRow>
      )}

      <div className="mb-3">
        <CFormLabel>Investment ($)</CFormLabel>
        <CFormInput
          type="number"
          value={investmentAmount}
          onChange={(e) => onChange("investmentAmount", Number(e.target.value))}
          disabled={disabled}
        />
      </div>

      <div className="mb-3">
        <CFormSwitch
          id="cumulativeInvestment"
          label="Cumulative Investment"
          checked={cumulativeInvestment}
          onChange={(e) => onChange("cumulativeInvestment", e.target.checked)}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
